/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

/*
 * TicketEditBaseComponent is the top level component for the various
 * child components (calendar picker, crew input, etc) placed inside
 * a container component (task, constrain, or milestone edit components).
 * This is an abstract class (no template), common things like the
 * "error state" go here and the child components inherit from this
 * class to get the common behaviors.
 *
 *  API convention:
 *
 * Components extending this class are meant to be embedded in a parent
 * component that has a parent form.  It's expected that the parent passes
 * it's FormGroup reference in as an Input to this component.  Further,
 * the parent must also configure the form control as part of creating the
 * form group, specicifically referring to individual components with the
 * name that's used in the specific validation and supplying a required
 * validator.  The parent can optionally supply an initial value too.
 *
 */

import {
	ChangeDetectionStrategy,
	Component,
	OnDestroy,
	OnInit,
	Self,
	Optional,
	ChangeDetectorRef,
} from '@angular/core';
import { UntypedFormControl, ControlValueAccessor, NgControl, AbstractControl } from '@angular/forms';
import { Subject, BehaviorSubject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TicketError, formErrorMapping } from '../../ticket-errors';
import { MultiWrapperComponent } from '../multi-wrapper/multi-wrapper.component';

@Component({
	selector: 'ticket-edit-base',
	template: '',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketEditBaseComponent implements OnDestroy, ControlValueAccessor, OnInit {
	public get showErrorPin() {
		return this.control && this.control.invalid && this.control.dirty;
	}

	constructor(
		@Self() @Optional() public control: NgControl,
		@Optional() public multiWrapper: MultiWrapperComponent,
		@Optional() protected cd: ChangeDetectorRef
	) {
		this.control && (this.control.valueAccessor = this);
	}

	// computed property to turn on the "error state" thing in the spec
	// override to add specific validation.
	// SUSPECT THIS IS NOT IN USE
	get invalid() {
		return true;
	}
	public shutdownSubject: Subject<boolean> = new Subject();

	private _errorState = new BehaviorSubject<TicketError>(null);
	public errorState: Observable<TicketError> = this._errorState.pipe(takeUntil(this.shutdownSubject));

	public internalField = new UntypedFormControl('');
	public showErrorDetail = false;

	private prevTouched = false;
	private prevDirty = false;

	// Both onChange and onTouched are functions
	public onChange: any = () => {};
	public onTouched: any = () => {};

	// Toggles the isMenuShown property
	public toggleShowErrorDetail() {
		this.showErrorDetail = !this.showErrorDetail;
	}

	public ngOnInit() {
		if (this.control) {
			formErrorMapping(this.control).pipe(takeUntil(this.shutdownSubject)).subscribe(this._errorState);
		}
	}

	public ngOnDestroy() {
		this.shutdownSubject.next(true);
	}

	public internalFocused() {
		if (this.multiWrapper) {
			this.multiWrapper.focus$.next(true);
		}
	}

	public internalBlurred() {
		this.onTouched();
		if (this.multiWrapper) {
			this.multiWrapper.focus$.next(false);
		}
	}

	public writeValue(obj: any) {
		this.internalField.setValue(obj, { emitEvent: false });
	}
	/**
	 * Registers a callback function that should be called when
	 * the control's value changes in the UI.
	 */
	public registerOnChange(fn: any) {
		this.onChange = fn;
	}
	/**
	 * Registers a callback function that should be called when
	 * the control receives a blur event.
	 */
	public registerOnTouched(fn: any) {
		this.onTouched = fn;
	}
	/**
	 * This function is called by the forms API when
	 * the control status changes to or from "DISABLED".
	 */
	public setDisabledState(isDisabled: boolean) {
		// logger.log('setDisabledState, just in case it breaks things', isDisabled, this);
		if (isDisabled) {
			this.internalField.disable({ emitEvent: false });
		} else {
			this.internalField.enable({ emitEvent: false });
		}
		//apparently disables don't fully apply without marking for change detection, everything except for setting ng-valid
		//emitEvent: false does not change this behavior
		//see src/app/common/custom-field-filter/components/custom-field-filter.component.spec.ts for unit test
		this.cd.markForCheck();
	}

	//applys a hack so that changedetection runs on control disabled/ touched changes
	public ngDoCheck() {
		if (!this.cd || !this.control) {
			return;
		}
		const changed = this.control.dirty !== this.prevDirty || this.control.touched !== this.prevTouched;
		this.prevDirty = this.control.dirty;
		this.prevTouched = this.control.touched;
		if (changed) {
			this.cd.markForCheck();
		}
	}
}
